import React, { useState, Suspense, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getConfigAPI, updateConfigAPI } from "../../API/ConfigAPI";
import ButtonLoader from "../../components/button/ButtonLoader";
import toast from "react-hot-toast";
import { FiSettings, FiMail, FiSave } from "react-icons/fi";

// Lazy load components
const Wrapper = React.lazy(() => import("../../components/wrapper/Wrapper"));

const configValidationSchema = Yup.object().shape({
  supportEmail: Yup.string()
    .email("Invalid email address")
    .required("Support email is required"),
});

const ConfigSettings = () => {
  const [loader, setLoader] = useState(false);
  const [updatingConfig, setUpdatingConfig] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);

  const initialValues = {
    supportEmail: "",
  };

  const { values, setValues, handleChange, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema: configValidationSchema,
      onSubmit: async (values) => {
        setUpdatingConfig(true);
        try {
          const res = await updateConfigAPI({
            supportEmail: values.supportEmail,
          });
          setCurrentConfig(res.data);
          setUpdatingConfig(false);
          setHasChanged(false);
          toast.success("Configuration updated successfully");
        } catch (error) {
          setUpdatingConfig(false);
          toast.error("Error updating configuration");
        }
      },
    });

  useEffect(() => {
    const fetchConfig = async () => {
      setLoader(true);
      try {
        const res = await getConfigAPI();
        setCurrentConfig(res.data);
        setValues({
          supportEmail: res.data?.supportEmail || "",
        });
      } catch (error) {
        toast.error("Failed to load configuration");
      } finally {
        setLoader(false);
      }
    };

    fetchConfig();
  }, [setValues]);

  const checkForChanges = (e) => {
    handleChange(e);
    const { name, value } = e.target;
    if (values[name] !== value) {
      setHasChanged(true);
    }
  };

  if (loader) {
    return (
      <div className="py-10 w-full flex justify-center items-center">
        <ButtonLoader />
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="w-full pt-40 flex justify-center items-center">
          <ButtonLoader />
        </div>
      }
    >
      <Wrapper>
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FiSettings className="w-8 h-8 text-[--primary]" />
            System Configuration
          </h1>
          <p className="text-gray-600 mt-2">
            Manage system-wide email address and configuration settings
          </p>
        </div>

        {/* Config Settings Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FiMail className="w-5 h-5" />
              Email Configuration
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure support email address used throughout the system
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* Last Updated Info */}
            {currentConfig?.updatedAt && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Last updated:</span>{" "}
                  {new Date(currentConfig.updatedAt).toLocaleString()}
                </p>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Support Email
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  name="supportEmail"
                  id="supportEmail"
                  type="email"
                  placeholder="support@tykoon.ai"
                  value={values.supportEmail}
                  onChange={checkForChanges}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                />
                {touched.supportEmail && errors.supportEmail && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.supportEmail}
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  This email will be displayed in privacy policy and used for
                  support inquiries
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!hasChanged || updatingConfig}
                className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {updatingConfig ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Wrapper>
    </Suspense>
  );
};

export default ConfigSettings;
